import { login } from 'features/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    useEmailAuthLoginMutation,
    usePhoneAuthLoginBypassMutation,
    usePhoneAuthLoginMutation,
    usePhoneConfirmationAuthLoginMutation
} from 'services/auth/auth.endpoint';
import { useApiToast } from 'components/Toaster';

export const useLoginPageVM = () => {
    const phoneLoginBypassNumber = '0615285412';
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const inputRef = useRef<any>(null);
    const { showErrorToast } = useApiToast();

    const [phoneAuthStep, setPhoneAuthStep] = useState<'phone' | 'code'>('phone');
    const [phoneValue, setPhoneValue] = useState<string>('');
    const [codeValue, setCodeValue] = useState<string>('');
    const [isComplete, setIsComplete] = useState<boolean>(false);
    const [isAppv, setIsAppv] = useState<boolean>(isMobile);
    const [isRoleChoice, setIsRoleChoice] = useState<boolean>(false);
    const [emailAuthErrorMessage, setEmailAuthErrorMessage] = useState<string>('');
    const [phoneAuthErrorMessage, setPhoneAuthErrorMessage] = useState<string>('');

    const [
        emailAuthLogin,
        {
            isLoading: emailAuthLoginLoading,
            isError: emailAuthLoginError,
            isSuccess: emailAuthLoginSuccess,
            error: emailAuthLoginResponseError,
            data: emailAuthLoginResponse
        }
    ] = useEmailAuthLoginMutation();

    const [
        phoneAuthLogin,
        {
            isLoading: phoneAuthLoginLoading,
            isError: phoneAuthLoginError,
            isSuccess: phoneAuthLoginSuccess,
            error: phoneAuthLoginResponseError,
            data: phoneAuthLoginResponse
        }
    ] = usePhoneAuthLoginMutation();

    const [
        phoneAuthLoginBypass,
        {
            isLoading: phoneAuthLoginBypassLoading,
            isError: phoneAuthLoginBypassError,
            isSuccess: phoneAuthLoginBypassSuccess,
            error: phoneAuthLoginBypassResponseError,
            data: phoneAuthLoginBypassResponse
        }
    ] = usePhoneAuthLoginBypassMutation();

    const [
        phoneConfirmationAuthLogin,
        {
            isLoading: phoneConfirmationAuthLoginLoading,
            isError: phoneConfirmationAuthLoginError,
            isSuccess: phoneConfirmationAuthLoginSuccess,
            error: phoneConfirmationAuthLoginResponseError,
            data: phoneConfirmationAuthLoginResponse
        }
    ] = usePhoneConfirmationAuthLoginMutation();

    const handleForgotPassword = () => {
        navigate('/forgot-password');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.target && (event.target as HTMLElement).closest('.pad')) {
            return;
        }

        if (event.isTrusted && /^\d$/.test(event.key)) {
            setPhoneValue((prev) => {
                if (prev.length < 10) {
                    return prev + event.key;
                }
                return prev;
            });
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (phoneAuthErrorMessage) {
            setPhoneAuthErrorMessage('');
        }
    }, [phoneValue]);

    const handleNumberClick = (number: string) => {
        const setValue = phoneAuthStep === 'phone' ? setPhoneValue : setCodeValue;
        const maxLength = phoneAuthStep === 'phone' ? 10 : 4;

        setValue((prev) => {
            if (prev.length < maxLength) {
                const newValue = prev + number;
                if (inputRef.current) {
                    inputRef.current.focus();
                }
                return newValue;
            }
            return prev;
        });
    };

    const handleDelete = () => {
        const setValue = phoneAuthStep === 'phone' ? setPhoneValue : setCodeValue;

        setValue((prev) => {
            const newValue = prev.slice(0, -1);
            if (inputRef.current) {
                inputRef.current.focus();
            }
            return newValue;
        });
    };

    const handleComplete = useCallback(
        (value: string) => {
            const maxLength = phoneAuthStep === 'phone' ? 10 : 4;
            if (value.length === maxLength) {
                setIsComplete(true);
            } else {
                setIsComplete(false);
            }
        },
        [phoneAuthStep]
    );

    const handleSubmit = async () => {
        if (phoneAuthStep === 'phone') {
            if (phoneValue === phoneLoginBypassNumber) {
                const response = await phoneAuthLoginBypass({ phone: phoneValue });
                if (response.error) {
                    showErrorToast(response.error, {
                        message: 'Numéro de téléphone non reconnu'
                    });
                }
                return;
            }

            await phoneAuthLogin({ phone: phoneValue });
            setPhoneAuthStep('code');
            setIsComplete(false);
        } else {
            const response = await phoneConfirmationAuthLogin({ phone: phoneValue, code: codeValue });
            if (response.error) {
                showErrorToast(response.error, {
                    message: 'Le code de vérification est invalide, merci de réessayer.'
                });
                setCodeValue('');
            }
        }
    };

    useEffect(() => {
        if (phoneAuthStep === 'phone') {
            if (phoneValue.length === 10) {
                handleComplete(phoneValue);
            } else {
                setIsComplete(false);
            }
        } else {
            if (codeValue.length === 4) {
                handleComplete(codeValue);
            } else {
                setIsComplete(false);
            }
        }
    }, [phoneValue, codeValue, phoneAuthStep, handleComplete]);

    useEffect(() => {
        if (emailAuthLoginLoading) return;
        if (emailAuthLoginError && emailAuthLoginResponseError) {
            if ('data' in emailAuthLoginResponseError) {
                const errorData = emailAuthLoginResponseError.data as { code: string };
                setEmailAuthErrorMessage(errorData.code);
            } else {
                setEmailAuthErrorMessage('Une erreur est survenue');
            }
            return;
        }
        if (emailAuthLoginSuccess && emailAuthLoginResponse) {
            setEmailAuthErrorMessage('');
            dispatch(login({ ...emailAuthLoginResponse.data.user, jwt: emailAuthLoginResponse.data.jwt }));
            navigate(isMobile ? '/mobile' : '/app/admin');
            return;
        }
    }, [
        emailAuthLoginLoading,
        emailAuthLoginError,
        emailAuthLoginSuccess,
        emailAuthLoginResponseError,
        emailAuthLoginResponse,
        dispatch,
        navigate
    ]);

    useEffect(() => {
        if (phoneAuthLoginLoading) return;
        if (phoneAuthLoginError && phoneAuthLoginResponseError) {
            return;
        }
        if (phoneAuthLoginSuccess && phoneAuthLoginResponse) {
            //setCodeValue(String(phoneAuthLoginResponse.data.code));
            return;
        }
    }, [phoneAuthLoginLoading, phoneAuthLoginError, phoneAuthLoginSuccess, phoneAuthLoginResponseError, phoneAuthLoginResponse]);

    useEffect(() => {
        if (phoneAuthLoginBypassLoading) return;
        if (phoneAuthLoginBypassError && phoneAuthLoginBypassResponseError) {
            return;
        }
        if (phoneAuthLoginBypassSuccess && phoneAuthLoginBypassResponse) {
            dispatch(login({ ...phoneAuthLoginBypassResponse.data.user, jwt: phoneAuthLoginBypassResponse.data.jwt }));
            navigate('/app/user');
            return;
        }
    }, [
        phoneAuthLoginBypassLoading,
        phoneAuthLoginBypassError,
        phoneAuthLoginBypassSuccess,
        phoneAuthLoginBypassResponseError,
        phoneAuthLoginBypassResponse,
        dispatch,
        navigate
    ]);

    const isPhoneAuthLoginLoading = phoneAuthLoginLoading || phoneAuthLoginBypassLoading;

    useEffect(() => {
        if (phoneConfirmationAuthLoginLoading) return;
        if (phoneConfirmationAuthLoginError && phoneConfirmationAuthLoginResponseError) {
            return;
        }
        if (phoneConfirmationAuthLoginSuccess && phoneConfirmationAuthLoginResponse) {
            dispatch(login({ ...phoneConfirmationAuthLoginResponse.data.user, jwt: phoneConfirmationAuthLoginResponse.data.jwt }));
            navigate('/app/user');
            return;
        }
    }, [
        phoneConfirmationAuthLoginLoading,
        phoneConfirmationAuthLoginError,
        phoneConfirmationAuthLoginSuccess,
        phoneConfirmationAuthLoginResponseError,
        phoneConfirmationAuthLoginResponse,
        dispatch,
        navigate
    ]);

    return {
        emailAuthLogin,
        phoneAuthLogin,
        emailAuthLoginLoading,
        emailAuthLoginError,
        emailAuthLoginSuccess,
        emailAuthLoginResponseError,
        emailAuthLoginResponse,
        phoneAuthLoginLoading: isPhoneAuthLoginLoading,
        phoneAuthLoginError,
        phoneAuthLoginSuccess,
        phoneAuthLoginResponseError,
        phoneAuthLoginResponse,
        phoneValue,
        setPhoneValue,
        isComplete,
        isAppv,
        setIsAppv,
        inputRef,
        handleNumberClick,
        handleDelete,
        handleSubmit,
        phoneAuthStep,
        codeValue,
        setCodeValue,
        handleForgotPassword,
        emailAuthErrorMessage,
        phoneAuthErrorMessage,
        isRoleChoice,
        setIsRoleChoice
    };
};
